import React, { FC, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { DataGridPro, GridColDef, LoadingOverlayPropsOverrides, useGridApiRef } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { IAccrualFutureBalanceDTO } from 'src/api/dtos';
import { DataGridLoadingOverlay, DataGridNoRowsOverlay, GenericDataGridStyle } from 'src/shared/components';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatTime } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

const GRID_ROW_HEIGHT = 36;

interface Props {
  rows: IAccrualFutureBalanceDTO[];
  loading?: boolean;
}

export const AccrualSummaryFutureAbsencesGrid: FC<Props> = ({ rows, loading }) => {
  const { t } = useTranslate();
  const apiRef = useGridApiRef();
  const timeFormat = useSelector(getTimeFormat);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'startDate',
        headerName: t('global.startDate'),
        sortable: false,
        flex: 1,
        renderCell: (params) => moment(params.value).format(DATE_FORMAT),
      },
      {
        field: 'endDate',
        headerName: t('global.endDate'),
        sortable: false,
        flex: 1,
        renderCell: (params) => moment(params.value).format(DATE_FORMAT),
      },
      {
        field: 'startTime',
        headerName: t('global.startTime'),
        sortable: false,
        flex: 1,
        renderCell: (params) => (params.value ? formatTime(params.value, timeFormat, DATE_TIME_FORMAT) : undefined),
      },
      {
        field: 'endTime',
        headerName: t('global.endTime'),
        sortable: false,
        flex: 1,
        renderCell: (params) => (params.value ? formatTime(params.value, timeFormat, DATE_TIME_FORMAT) : undefined),
      },
      {
        field: 'timeCategoryName',
        headerName: t('absences.absenceType'),
        sortable: false,
        flex: 1,
        renderCell: (params) => params.value,
      },
    ];
  }, [timeFormat]);

  return (
    <Stack direction="column" spacing={2}>
      <Typography fontWeight={600} variant="body1" color="primary">
        {t('accrualSummary.futureAbsences')} ({rows.length})
      </Typography>

      <div>
        <StyledDataGrid
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          slots={{
            noRowsOverlay: DataGridNoRowsOverlay,
            loadingOverlay: DataGridLoadingOverlay,
          }}
          slotProps={{ loadingOverlay: { isEmpty: !rows.length } as LoadingOverlayPropsOverrides }}
          // Sorting
          sortingMode="client"
          sortingOrder={['asc', 'desc']}
          // Settings
          hideFooter
          disableColumnMenu
          disableColumnReorder
          disableMultipleRowSelection
          disableRowSelectionOnClick
          disableColumnResize
          rowSelection={false}
          isRowSelectable={() => false}
          // Style
          columnHeaderHeight={GRID_ROW_HEIGHT}
          rowHeight={GRID_ROW_HEIGHT}
          sx={{
            border: 'none',
            gap: 0,
            height: !rows.length ? 180 : 'auto',
            maxWidth: { md: '100%', lg: 720 },
            '.MuiDataGrid-main': {
              borderRadius: '8px 8px 0 0',
            },
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              width: !rows.length ? 0 : 8,
              height: 0, // Hide horizontal scroll bar on data grid by setting height: 0
            },
          }}
        />
      </div>
    </Stack>
  );
};

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  ...GenericDataGridStyle(theme),
}));
